import { getDefaultValueByType } from "@salestrak/custom-fields";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Dialog,
  DialogFooter,
  DialogPopup,
  DialogTitle,
  EmptyState,
  Icon,
  ScrollArea,
  Skeleton,
  Text,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  useOptionalAutoFocus,
  usePrefersReducedMotion,
} from "@salestrak/ui";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { isCustomFieldValueEmpty } from "#app/components/custom-fields/custom-field-renderers";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { EntityBreadcrumbLink } from "#app/components/entity";
import { hasFormBanner } from "#app/components/forms/form-banner";
import { FormFillCardsSkeleton } from "#app/components/forms/form-skeletons";
import {
  useFormCatalogKnown,
  useFormFieldCatalog,
  useStageScopedCatalog,
} from "#app/components/forms/use-form-field-catalog";
import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useFormBuilderDialogActions } from "#app/lib/form-builder-dialog-context";
import {
  useFormFillDialogActions,
  type FormFillIssue,
  type FormFillTarget,
} from "#app/lib/form-fill-dialog-context";
import { useIssueById } from "#app/lib/hooks/use-issue-by-id";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  byNewestActivity,
  submittedOf,
  type Form,
  type FormProperty,
  type FormSubmission,
  type FormsStore,
  answersOnPath,
  formSteps,
  formVisibility,
  nextAskedStep,
  walkForm,
  hasSubmissionAnswers,
  isFieldItem,
  isFillableForm,
  isOfferedOn,
  isOncePerProject,
  isSubmissionLimitReached,
  linkedDefaultAnswer,
  linkedPrefills,
  offersResubmit,
  submissionLimitOf,
  newSubmission,
  resolveItemField,
  stringIds,
  submitLabelOf,
  type SubmissionErrors,
  useFormsStore,
  validateSubmission,
} from "#app/lib/forms";
import { getInitialCustomFieldValue } from "#app/lib/custom-field-utils";

import {
  FormFillBody,
  FormFillColumn,
  FormFillReview,
  formItemDomId,
  FormStepRail,
  FORM_SUBMIT_MS,
  FormSubmitOutcome,
  FormTitleCard,
  cantSubmitToast,
} from "./form-fill-body";
import {
  type AnswerUpdate,
  FormPhotoUploadsProvider,
  useFormPhotoUploads,
} from "./form-photo-uploads";
import { FormSubmissionRead, SubmissionStatement } from "./form-submission-read";
import { SubmissionShareDialog } from "./submission-share-dialog";
import { propertyValueOf } from "./property-value-of";

function propertyItemId(form: Pick<Form, "items">, property: FormProperty): string | null {
  return (
    form.items.find((item) => item.kind === "property" && item.property === property)?.itemId ??
    null
  );
}

const REVIEW = -1;

type FormProgress = { done: number; left: number; submissionId: string };

export function FormFillDialog({
  open,
  target,
  onClose: closeWindow,
}: {
  open: boolean;
  target: FormFillTarget | null;
  onClose: () => void;
}) {
  // An uploading photo dies with the content column, so anything that removes the column asks first.
  const [uploading, setUploading] = useState(0);
  const [progress, setProgress] = useState<FormProgress | null>(null);
  const [leaving, setLeaving] = useState<(() => void) | null>(null);
  const guardLeave = useCallback(
    (leave: () => void) => {
      if (uploading > 0) setLeaving(() => leave);
      else leave();
    },
    [uploading],
  );
  const onClose = useCallback(() => guardLeave(closeWindow), [closeWindow, guardLeave]);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { currentUser } = useAppData();
  const userId = currentUser?.userId ?? "";
  const store = useFormsStore(target?.kind === "submission" ? target.issueId : (target?.issue.issueId ?? null), { skip: !target });
  const customFieldsById = useFormFieldCatalog();

  // Minted locally once per target; the ref (not state) guards against StrictMode's doubled effect.
  const [pending, setPending] = useState<FormSubmission | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sharingId, setSharingId] = useState<string | null>(null);
  const sharing = store.submissions.find((row) => row.submissionId === sharingId) ?? null;
  const mintedFor = useRef<FormFillTarget | null>(null);

  useEffect(() => {
    if (!target) {
      mintedFor.current = null;
      setPending(null);
      setSelectedId(null);
      return;
    }
    if (mintedFor.current === target || store.formsLoading || store.submissionsLoading) return;
    mintedFor.current = target;
    setPending(null);
    if (target.kind === "submission") {
      setSelectedId(target.submissionId);
      return;
    }
    const targetForm = store.formsById.get(target.formId);
    const limit = targetForm ? submissionLimitOf(targetForm) : null;
    if (targetForm && limit !== null) {
      // A limited form at its limit opens its newest submission; a once-per-Project form picks up its newest unfinished one.
      const onProject = store.submissions
        .filter((row) => row.formId === target.formId)
        .sort(byNewestActivity);
      const submitted = onProject.filter((row) => row.status === "submitted");
      const limitReached = isSubmissionLimitReached(targetForm, submitted.length);
      if (limitReached) {
        toastManager.add({
          title: "You've reached the submission limit",
          description:
            limit === 1
              ? "This Project takes one submission of this form."
              : `This Project takes ${limit} submissions of this form.`,
          type: "info",
        });
      }
      const held = limitReached
        ? submitted[0]
        : isOncePerProject(targetForm)
          ? onProject.find((row) => row.viewerCanOpen)
          : undefined;
      if (held) {
        setSelectedId(held.submissionId);
        return;
      }
    }
    if (!targetForm) {
      setSelectedId(null);
      return;
    }
    const draft = newSubmission(targetForm, { createdBy: userId, issueId: target.issue.issueId });
    setPending(draft);
    setSelectedId(draft.submissionId);
  }, [store, target, userId]);

  const submission = useMemo(
    () =>
      store.submissions.find((row) => row.submissionId === selectedId) ??
      (pending && pending.submissionId === selectedId ? pending : null),
    [pending, selectedId, store.submissions],
  );
  // A filling asks the questions of the version it was started on.
  const currentForm =
    (submission ? store.formsById.get(submission.formId) : null) ??
    (target && target.kind !== "submission" ? store.formsById.get(target.formId) : null) ??
    null;
  const versionItems = submission?.items;
  const form = useMemo(
    () => (currentForm && versionItems ? { ...currentForm, items: versionItems } : currentForm),
    [currentForm, versionItems],
  );
  const locked =
    form != null &&
    submission != null &&
    submission !== pending &&
    !submission.viewerCanOpen;

  // Emptying a stored submission deletes the row but keeps it on screen as the local one, else the dialog reads "no longer available".
  const onSubmissionEmptied = useCallback((row: FormSubmission) => setPending(row), []);
  const [refused, setRefused] = useState<{ submissionId: string; message: string } | null>(null);
  const refusal = refused && refused.submissionId === submission?.submissionId ? refused.message : null;
  const onSubmissionRefused = useCallback(
    (submissionId: string, message: string) => setRefused({ message, submissionId }),
    [],
  );

  const { issue: loadedIssue, loading: issueLoading } = useIssueById(
    target?.kind === "submission" ? submission?.issueId : undefined,
  );
  const issue: FormFillIssue | null =
    target && target.kind !== "submission" ? target.issue : loadedIssue;
  // Reads as able until the Project row loads, like the issue view.
  const canFill = issue?.viewerCanEdit !== false;
  // Answers seed once on mount, so wait for the catalog.
  const catalogKnown = useFormCatalogKnown(form?.items ?? []);
  const waiting = !catalogKnown || (issueLoading && !loadedIssue);

  // The fill dialog closes before the builder opens so the two windows do not stack.
  const canEditForm = usePermission("forms.manage");
  const { openFormBuilder } = useFormBuilderDialogActions();

  const editAction =
    form && canEditForm ? (
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              aria-label="Edit form"
              onClick={() => {
                onClose();
                openFormBuilder(form.formId);
              }}
              color="neutral"
              size="icon-xs"
            />
          }
        >
          <Icon name="pencil" />
        </TooltipTrigger>
        <TooltipPopup>Edit form</TooltipPopup>
      </Tooltip>
    ) : null;

  // Only a stored filling can be handed on; the store keeps no blank row.
  const stored = submission
    ? store.submissions.some((row) => row.submissionId === submission.submissionId)
    : false;
  const shareButton = (
    <Button
      disabled={!stored}
      onClick={() => submission && setSharingId(submission.submissionId)}
      color="neutral"
      size="xs"
    >
      <Icon name="share" size="xs" />
      Share
    </Button>
  );
  const shareAction =
    !submission || locked || !form || formVisibility(form) === "public" ? null : stored ? (
    shareButton
  ) : (
    // A disabled Button is pointer-events-none, so the hover that opens the
    // tooltip lands on this wrapper instead.
    <Tooltip>
      <TooltipTrigger render={<Box cursor="not-allowed" direction="row" />}>
        {shareButton}
      </TooltipTrigger>
      <TooltipPopup>Answer a question first, then share it</TooltipPopup>
    </Tooltip>
  );

  return (
    <Dialog
      onOpenChange={(next) => (next ? undefined : onClose())}
      open={open}
      sheetSize="full"
    >
      {/* Focus rests on the popup so Escape targets this dialog, not the detail popup behind it. */}
      <DialogPopup
        initialFocus={() => popupRef.current ?? true}
        ref={popupRef}
        showCloseButton={false}
        size="window"
        surface="background"
      >
        <DialogTitle className="sr-only">{form?.name ?? "Form"}</DialogTitle>
        {form ? (
          <FormFillHeader
            answering={canFill && submission?.status === "inProgress"}
            editAction={editAction}
            form={form}
            onClose={onClose}
            progress={progress}
            projectName={issue?.name ?? ""}
            projectId={submission?.issueId ?? issue?.issueId ?? ""}
            workflowId={issue?.workflowId ?? null}
            shareAction={shareAction}
          />
        ) : (
          <ManageDialogHeader onClose={onClose}>
            <Text size="sm" weight="semibold">
              Form
            </Text>
          </ManageDialogHeader>
        )}
        <Box direction="column" fill minWidth={0}>
          {form && submission && !locked && !refusal && waiting ? (
            <FormFillDialogSkeleton form={form} submission={submission} />
          ) : form && submission && !locked && !refusal ? (
            <FormFillDialogContent
              canFill={canFill}
              customFieldsById={customFieldsById}
              form={form}
              issue={issue}
              key={submission.submissionId}
              onEmptied={onSubmissionEmptied}
              onRefused={onSubmissionRefused}
              onProgressChange={setProgress}
              onUploadingChange={setUploading}
              store={store}
              submission={submission}
            />
          ) : (
            <>
              <Box align="center" fill justify="center" padding="lg">
                {store.formsLoading || store.submissionsLoading ? null : locked ? (
                  <EmptyState
                    description="It's private. Only the people it's shared with can open it."
                    title="You can't open this submission"
                  />
                ) : refusal ? (
                  <EmptyState description={refusal} title="Your answers can't be saved" />
                ) : (
                  <EmptyState title="This submission is no longer available." />
                )}
              </Box>
              <DialogFooter>
                <Button onClick={onClose} variant="outline">
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </Box>
      </DialogPopup>
      <SubmissionShareDialog
        canRemove={sharing?.viewerCanRemoveShares ?? false}
        formName={form?.name ?? ""}
        onChange={store.setSubmissionShares}
        onOpenChange={(next) => {
          if (!next) setSharingId(null);
        }}
        submission={sharing}
      />
      <AlertDialog
        onOpenChange={(next) => {
          if (!next) setLeaving(null);
        }}
        open={leaving !== null}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {uploading === 1 ? "A photo is still uploading" : `${uploading} photos are still uploading`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Leave now and {uploading === 1 ? "it isn't" : "they aren't"} added to this form. Stay
              and {uploading === 1 ? "it lands" : "they land"} in a moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Stay</AlertDialogClose>
            <Button
              onClick={() => {
                leaving?.();
                setLeaving(null);
              }}
              color="destructive"
            >
              Leave anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Dialog>
  );
}

function FormFillHeader({
  answering,
  form,
  projectId,
  workflowId,
  projectName,
  shareAction,
  editAction,
  onClose,
  progress,
}: {
  answering: boolean;
  progress: FormProgress | null;
  form: Form;
  workflowId: string | null;
  projectId: string;
  projectName: string;
  shareAction: ReactNode;
  editAction: ReactNode;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const { workflows } = useAppData();
  const workflow = workflowId ? workflows.find((row) => row.workflowId === workflowId) : undefined;
  return (
    <ManageDialogHeader
      actions={
        <>
          {shareAction}
          {editAction}
        </>
      }
      onClose={onClose}
    >
      <Box align="center" direction="row" gap="sm" grow minWidth={0}>
        {answering ? (
          progress ? (
            // Keyed on the filling so the line never falls while a filler moves forward.
            <FormStepRail done={progress.done} key={progress.submissionId} left={progress.left} />
          ) : null
        ) : (
          <>
            <Breadcrumb>
              <BreadcrumbList>
                {projectName ? (
                  <>
                    {workflow && !isMobile ? (
                      <>
                        <BreadcrumbItem>
                          <EntityBreadcrumbLink
                            collapsed
                            entity={{ id: workflow.workflowId, kind: "workflow", workflow }}
                          />
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                      </>
                    ) : null}
                    <BreadcrumbItem>
                      <EntityBreadcrumbLink
                        collapsed={isMobile}
                        entity={{ id: projectId, issue: { name: projectName }, kind: "issue" }}
                        onClick={onClose}
                      />
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                ) : null}
                <BreadcrumbItem>
                  <BreadcrumbPage>{form.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </>
        )}
      </Box>
    </ManageDialogHeader>
  );
}

function FormFillTitle({ form, answering, children }: { form: Form; answering: boolean; children?: ReactNode }) {
  if (answering || hasFormBanner(form)) return <FormTitleCard form={form}>{children}</FormTitleCard>;
  return (
    <Box direction="column" gap="2xs" paddingBottom="xs" paddingTop="sm">
      <Text size="2xl" weight="semibold">
        {form.name}
      </Text>
      {form.description ? (
        <Text pre size="sm" variant="muted">
          {form.description}
        </Text>
      ) : null}
      {children}
    </Box>
  );
}

function FormFillDialogSkeleton({
  form,
  submission,
}: {
  form: Form;
  submission: FormSubmission;
}) {
  const answering = submission.status === "inProgress";
  return (
    <>
      <ScrollArea className="[container-type:size]">
        <Box className="pb-[50cqh]" paddingTop="md" paddingX="lg">
          <FormFillColumn>
            <FormFillTitle answering={answering} form={form} />
            <FormFillCardsSkeleton count={form.items.length} />
          </FormFillColumn>
        </Box>
      </ScrollArea>
      {answering ? (
        <DialogFooter className="in-data-[slot=drawer-popup]:mt-0!">
          <Box align="center" direction="row" grow justify="end">
            <Skeleton className="rounded-md" delayed height={32} width={72} />
          </Box>
        </DialogFooter>
      ) : null}
    </>
  );
}

function FormFillDialogContent({
  canFill,
  form,
  submission,
  issue,
  customFieldsById: catalog,
  store,
  onEmptied,
  onRefused,
  onProgressChange,
  onUploadingChange,
}: {
  canFill: boolean;
  form: Form;
  submission: FormSubmission;
  issue: FormFillIssue | null;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  store: FormsStore;
  onUploadingChange: (count: number) => void;
  onProgressChange: (progress: FormProgress | null) => void;
  onEmptied: (submission: FormSubmission) => void;
  onRefused: (submissionId: string, message: string) => void;
}) {
  const isMobile = useIsMobile();
  const fillActions = useFormFillDialogActions();

  // Linked items seed once from the Project's values; only the catalog knows a workspace field's type.
  const [{ answers: seededAnswers, prefilled }] = useState(() => {
    const answers = { ...submission.answers };
    const taken: Record<string, unknown> = { ...submission.prefilled };
    for (const item of form.items) {
      if (!isFieldItem(item) || item.itemId in answers) continue;
      const field = resolveItemField(item, catalog);
      if (!field) continue;
      const prefills =
        (item.kind !== "customField" && item.kind !== "property") || linkedPrefills(item);
      const onProject =
        !issue ||
        !canFill ||
        !prefills ||
        submission.status !== "inProgress" ||
        field.type === "photos"
          ? undefined
          : item.kind === "property"
            ? propertyValueOf(issue, item.property)
            : item.kind === "customField"
              ? issue.values?.[item.customFieldId]
              : undefined;
      // A linked choice's form default stands under the Project's value, over the field's own default.
      const formDefault =
        item.kind === "customField" || item.kind === "property"
          ? linkedDefaultAnswer(item, field)
          : null;
      if (onProject !== undefined && !isCustomFieldValueEmpty(field, onProject)) {
        answers[item.itemId] = onProject;
        taken[item.itemId] = onProject;
      } else if (formDefault) {
        answers[item.itemId] = formDefault;
      } else if (item.kind === "customField" && prefills) {
        answers[item.itemId] = getInitialCustomFieldValue(field);
      } else {
        answers[item.itemId] = getDefaultValueByType(field.type);
      }
    }
    return {
      answers,
      prefilled: Object.keys(taken).length > 0 ? taken : undefined,
    };
  });

  const [localAnswers, setLocalAnswers] = useState(seededAnswers);
  // A Workflow and Stage on one form are one move: Stages come from the workflow the Project will be in.
  const workflowItemId = propertyItemId(form, "workflow");
  const stageItemId = propertyItemId(form, "stage");
  const [answeredWorkflowId] = stringIds(workflowItemId ? localAnswers[workflowItemId] : undefined);
  const stageWorkflowId = answeredWorkflowId ?? issue?.workflowId ?? null;
  const customFieldsById = useStageScopedCatalog(catalog, stageWorkflowId);
  const { stageMap } = useWorkspaceData();
  const [errors, setErrors] = useState<SubmissionErrors>({});
  const answering = canFill && submission.status === "inProgress";

  const steps = useMemo(() => formSteps(form), [form]);
  // Pages walked, newest last, so Back retraces the walk past blank pages; the review is an entry too.
  const [history, setHistory] = useState<number[]>([0]);
  const reviewing = history.at(-1) === REVIEW;
  const stepIndex = Math.min(
    history.findLast((entry) => entry !== REVIEW) ?? 0,
    steps.length - 1,
  );
  const paged = answering && steps.length > 1;
  const step = steps[stepIndex];
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollToBody = () => bodyRef.current?.scrollIntoView({ block: "start" });
  const goToStep = (index: number) => {
    setHistory((current) => [...current, index]);
    scrollToBody();
  };
  const goBack = () => {
    setHistory((current) => (current.length > 1 ? current.slice(0, -1) : current));
    scrollToBody();
  };
  // What Submit validates and keeps: a question a condition hid was never asked.
  const walk = useMemo(
    () => walkForm({ items: steps.flatMap((step) => step.items) }, localAnswers, customFieldsById),
    [customFieldsById, localAnswers, steps],
  );
  const { path, items: walkedItems } = walk;
  const pageItems = useMemo(
    () => (step?.items ?? []).filter((item) => !walk.hidden.has(item.itemId)),
    [step, walk.hidden],
  );
  const atEnd = nextAskedStep(steps, stepIndex, walk.hidden) === "submit";

  const done = history.filter((entry) => entry !== REVIEW).length - 1;
  const left = path.length - Math.max(path.indexOf(stepIndex), 0);
  const showsProgress = paged && !reviewing;
  const { submissionId } = submission;
  useEffect(() => {
    onProgressChange(showsProgress ? { done, left, submissionId } : null);
    return () => onProgressChange(null);
  }, [done, left, onProgressChange, showsProgress, submissionId]);
  const [saved, setSaved] = useState(false);

  // Persist runs after the answers commit: a store write inside the updater is a setState mid-render.
  const answersDirty = useRef(false);
  // Each save names only the questions answered here since the server last confirmed one, so a second open copy never undoes this one.
  const unsaved = useRef(new Map<string, number>());
  const edits = useRef(0);
  const answersRef = useRef(localAnswers);
  answersRef.current = localAnswers;
  const [revisions, setRevisions] = useState<ReadonlyMap<string, number>>(() => new Map());
  const adoptStored = useCallback((stored: Record<string, unknown>) => {
    const current = answersRef.current;
    const changed = [...new Set([...Object.keys(stored), ...Object.keys(current)])].filter(
      (itemId) => !unsaved.current.has(itemId) && JSON.stringify(stored[itemId]) !== JSON.stringify(current[itemId]),
    );
    if (changed.length === 0) return;
    setLocalAnswers((answers) => {
      const next = { ...answers };
      for (const itemId of changed) {
        if (itemId in stored) next[itemId] = stored[itemId];
        else delete next[itemId];
      }
      return next;
    });
    setRevisions((known) => new Map([...known, ...changed.map((itemId) => [itemId, (known.get(itemId) ?? 0) + 1] as const)]));
  }, []);
  useEffect(() => {
    if (!answersDirty.current) return;
    answersDirty.current = false;
    if (!answering) return;
    const row = { ...submission, answers: localAnswers, prefilled: prefilled ?? null };
    const sent = new Map(unsaved.current);
    setSaved(false);
    store.saveSubmission(
      row,
      (stored) => {
        for (const [itemId, edit] of sent) {
          if (unsaved.current.get(itemId) === edit) unsaved.current.delete(itemId);
        }
        if (!hasSubmissionAnswers(stored, prefilled)) {
          onEmptied(row);
          return;
        }
        adoptStored(stored);
        setSaved(true);
      },
      [...sent.keys()],
      (message) => onRefused(row.submissionId, message),
    );
  }, [adoptStored, answering, localAnswers, onEmptied, onRefused, prefilled, store, submission]);
  const updateAnswer = useCallback<AnswerUpdate>(
    (itemId, compose) => {
      setLocalAnswers((current) => {
        const dtoValue = compose(current[itemId]);
        if (JSON.stringify(dtoValue) === JSON.stringify(current[itemId])) return current;
        answersDirty.current = true;
        const next = { ...current, [itemId]: dtoValue };
        unsaved.current.set(itemId, ++edits.current);
        // Moving the Workflow off the answered Stage's workflow clears that Stage answer.
        if (itemId === workflowItemId && stageItemId) {
          const [stageId] = stringIds(next[stageItemId]);
          const [workflowId] = stringIds(dtoValue);
          const landsIn = workflowId ?? issue?.workflowId;
          if (stageId && stageMap.get(stageId)?.workflowId !== landsIn) {
            next[stageItemId] = [];
            unsaved.current.set(stageItemId, ++edits.current);
          }
        }
        return next;
      });
      setErrors((current) => {
        if (!(itemId in current)) return current;
        const { [itemId]: _cleared, ...rest } = current;
        return rest;
      });
    },
    [issue?.workflowId, stageItemId, stageMap, workflowItemId],
  );
  const onAnswerChange = useCallback(
    (itemId: string, dtoValue: unknown) => updateAnswer(itemId, () => dtoValue),
    [updateAnswer],
  );

  // Uploads live here so paging on Next never cancels one.
  const photoUploads = useFormPhotoUploads(updateAnswer);
  const uploading = photoUploads.pending.length;
  useEffect(() => {
    onUploadingChange(uploading);
    return () => onUploadingChange(0);
  }, [onUploadingChange, uploading]);

  // Next never blocks: blanks are flagged and Submit is the one gate.
  const onNext = () => {
    const found = validateSubmission(pageItems, localAnswers, customFieldsById);
    setErrors((current) => ({ ...current, ...found }));
    const next = nextAskedStep(steps, stepIndex, walk.hidden);
    if (next !== "submit") goToStep(next);
  };
  const pageOf = (itemId: string) =>
    Math.max(
      steps.findIndex((row) => row.items.some((item) => item.itemId === itemId)),
      0,
    );
  const missed = walkedItems.filter((item) => item.itemId in errors);
  const here = path.indexOf(stepIndex);
  const nextMissed =
    missed.find((item) => path.indexOf(pageOf(item.itemId)) >= here) ?? missed[0];

  // A jump onto another page lands without gliding; a scroll from the old position reads as lurching.
  const reducedMotion = usePrefersReducedMotion();
  const seatCaret = useOptionalAutoFocus();
  const land = (itemId: string, glide: boolean) => {
    const card = document.getElementById(formItemDomId(itemId));
    card?.scrollIntoView({ behavior: glide && !reducedMotion ? "smooth" : "auto", block: "center" });
    if (seatCaret) {
      card?.querySelector<HTMLElement>("input, textarea, button")?.focus({ preventScroll: true });
    }
  };
  const landing = useRef<string | null>(null);
  const jumpTo = (itemId: string) => {
    const page = pageOf(itemId);
    if (!reviewing && page === stepIndex) {
      land(itemId, true);
      return;
    }
    landing.current = itemId;
    setHistory((current) => [...current, page]);
  };
  useEffect(() => {
    const itemId = landing.current;
    if (!itemId) return;
    landing.current = null;
    land(itemId, false);
  });

  const onSubmit = () => {
    const found = validateSubmission(walkedItems, localAnswers, customFieldsById);
    setErrors(found);
    // A held Submit says why, even from the review, where a press would
    // otherwise change nothing on screen.
    const held = walkedItems.filter((item) => item.itemId in found);
    const [first] = held;
    if (first) {
      cantSubmitToast(held, found);
      if (!paged) jumpTo(first.itemId);
      else if (!reviewing) goToStep(REVIEW);
      return;
    }
    // The server re-checks the limit, so two people with a limited form open cannot both land.
    const submittedBefore = submittedOf(store.submissions, form.formId).filter(
      (row) => row.submissionId !== submission.submissionId,
    ).length;
    const answers = answersOnPath(localAnswers, walkedItems);
    setOutcome({ elapsed: false, landed: false, submitted: submittedBefore + 1 });
    void store.submit({ ...submission, answers, prefilled: prefilled ?? null }, [...unsaved.current.keys()]).then((result) => {
      if (!result.ok) {
        setOutcome(null);
        if (result.errors) setErrors(result.errors);
        toastManager.add({
          title: result.code === "SUBMISSION_LIMIT_REACHED" ? "Submission limit reached" : result.message,
          type: "error",
        });
        return;
      }
      setOutcome((current) => current && { ...current, landed: true });
      for (const noun of result.failedWrites) {
        toastManager.add({ title: `Failed to update ${noun}`, type: "error" });
      }
    });
  };
  const [outcome, setOutcome] = useState<{
    elapsed: boolean;
    landed: boolean;
    submitted: number;
  } | null>(null);
  const outcomePhase = outcome?.elapsed && outcome.landed ? "submitted" : "submitting";
  const awaitingAnimation = outcome !== null && !outcome.elapsed;
  useEffect(() => {
    if (!awaitingAnimation) return;
    const timer = setTimeout(
      () => setOutcome((current) => current && { ...current, elapsed: true }),
      FORM_SUBMIT_MS,
    );
    return () => clearTimeout(timer);
  }, [awaitingAnimation]);

  // A press while a photo uploads is kept, not refused, and runs when the last upload lands.
  const [queued, setQueued] = useState<"next" | "submit" | null>(null);
  const uploadingOn = (items: Form["items"]) =>
    photoUploads.pending.some((upload) => items.some((item) => item.itemId === upload.key));
  const onNextPress = () => (uploadingOn(pageItems) ? setQueued("next") : onNext());
  const onSubmitPress = () => (uploadingOn(walkedItems) ? setQueued("submit") : onSubmit());
  const runQueued = useEffectEvent(() => {
    if (!queued || uploadingOn(queued === "next" ? pageItems : walkedItems)) return;
    setQueued(null);
    if (queued === "next") onNext();
    else onSubmit();
  });
  useEffect(() => runQueued(), [queued, photoUploads.pending]);

  const statement = answering ? null : (
    <Box paddingTop="xs">
      <SubmissionStatement submission={submission} />
    </Box>
  );

  const fillsHere = canFill && issue != null && isFillableForm(form) && isOfferedOn(form, issue);
  const startsNew =
    fillsHere &&
    submission.status === "submitted" &&
    !isSubmissionLimitReached(form, submittedOf(store.submissions, form.formId).length);

  const body = reviewing ? (
    <FormFillReview
      customFieldsById={customFieldsById}
      errors={errors}
      hidden={walk.hidden}
      onJump={jumpTo}
      pages={path.flatMap((index) => steps[index] ?? [])}
    />
  ) : answering ? (
    <FormPhotoUploadsProvider value={photoUploads}>
      <FormFillBody
        answers={localAnswers}
        revisions={revisions}
        customFieldsById={customFieldsById}
        errors={errors}
        form={{ items: paged ? (step?.items ?? []) : form.items }}
        hidden={walk.hidden}
        onAnswerChange={onAnswerChange}
        prefilled={prefilled}
      />
    </FormPhotoUploadsProvider>
  ) : (
    <FormSubmissionRead
      customFieldsById={customFieldsById}
      form={form}
      submission={submission}
      // A submitted photo is the Project's, so deleting needs the edit access Attachments asks for.
      onDeletePhoto={
        canFill && submission.status === "submitted"
          ? (photoId) => {
              void store.removeFormPhotos(submission.issueId, [photoId]);
            }
          : undefined
      }
    />
  );

  if (outcome) {
    return (
      <FormSubmitOutcome
        actions={
          outcomePhase === "submitted" ? (
            <>
              <Button onClick={() => setOutcome(null)} variant="outline">
                View submission
              </Button>
              {fillsHere && issue && offersResubmit(form, outcome.submitted) ? (
                <Button
                  onClick={() => fillActions.open({ formId: form.formId, issue, kind: "new" })}
                >
                  Submit another
                </Button>
              ) : null}
            </>
          ) : null
        }
        form={form}
        phase={outcomePhase}
        submitted={outcome.submitted}
      />
    );
  }

  return (
    <>
      {/* Size container for the body's bottom gutter (50cqh). */}
      <ScrollArea className="[container-type:size]" scrollFade={!isMobile}>
        <Box className="pb-[50cqh]" paddingTop="md" paddingX="lg" ref={bodyRef}>
          <FormFillColumn>
            <FormFillTitle answering={answering} form={form}>
              {statement}
            </FormFillTitle>
            {body}
          </FormFillColumn>
        </Box>
      </ScrollArea>
      {startsNew ? (
        <DialogFooter className="in-data-[slot=drawer-popup]:mt-0!">
          <Button
            onClick={() => fillActions.open({ formId: form.formId, issue, kind: "new" })}
            size="sm"
          >
            New submission
          </Button>
        </DialogFooter>
      ) : null}
      {answering ? (
        <DialogFooter className="in-data-[slot=drawer-popup]:mt-0!">
          <Box align="center" direction="row" gap="sm" grow justify="end" wrap>
            {(!reviewing && nextMissed) || saved ? (
              <Box align="center" direction="row" gap="sm" grow>
                {!reviewing && nextMissed ? (
                  <Button onClick={() => jumpTo(nextMissed.itemId)} size="xs" variant="ghost">
                    <Icon color="warning" name="alert-triangle" size="xs" />
                    <Text size="xs" variant="warning" weight="medium">
                      {missed.length} missed
                    </Text>
                  </Button>
                ) : null}
                {saved ? (
                  <Text size="xs" variant="faint">
                    Saved
                  </Text>
                ) : null}
              </Box>
            ) : null}
            <Box align="center" direction="row" gap="xs">
              {history.length > 1 ? (
                <Button onClick={goBack} size="sm" variant="ghost">
                  {history.at(-2) === REVIEW ? "Back to review" : "Back"}
                </Button>
              ) : null}
              {paged && !atEnd && !reviewing ? (
                <Button disabled={queued !== null} onClick={onNextPress} size="sm">
                  {queued === "next" ? "Uploading photos…" : "Next"}
                </Button>
              ) : (
                <Button disabled={queued !== null} onClick={onSubmitPress} size="sm">
                  {queued === "submit" ? "Uploading photos…" : submitLabelOf(form)}
                </Button>
              )}
            </Box>
          </Box>
        </DialogFooter>
      ) : null}
    </>
  );
}
